// viewer.footprint.js — ground-projection footprint controller for the
// shared viewer. Exports a `createFootprintController()` factory plus a
// handful of pure functions (parser, binary search) so the
// logic can be unit-tested in Node.

// ---------- Constants (post-launch tweak targets) ----------
const FILL_COLOR = "#ff8c00";
const FILL_OPACITY = 0.18;
const STROKE_COLOR = "#ff8c00";
const STROKE_OPACITY = 1.0;
const STROKE_WEIGHT = 1.5;
// Distance fade: full opacity out to this fraction of the ray reach (3 km
// of the 5 km default), then a linear ramp to fully transparent AT the
// reach — so a horizon-clipped polygon has no visible back edge. A display
// choice, deliberately overriding the sidecar's legacy fadeStartFraction.
const FADE_START_FRACTION = 0.6;
const SVG_NS = "http://www.w3.org/2000/svg";
const R_EARTH_M = 6371000;

// ---------- Pure helpers (testable in Node) ----------

export function parseFootprintJSON(raw) {
  if (!raw || typeof raw !== "object") {
    throw new Error("Footprint: payload is not an object");
  }
  if (raw.schema !== 1 && raw.schema !== 2) {
    throw new Error(`Footprint: unsupported schema ${raw.schema}`);
  }
  const n = raw.sampleCount;
  // Map nulls (DEM miss / horizon clip corners — see baker._round_or_null)
  // to NaN inside the Float64Array so `Number.isFinite` works downstream.
  // Without the mapper, `Float64Array.from([null, …])` coerces null → 0
  // and the viewer would draw polygons at lat=0/lon=0.
  const toF64 = (arr) => Float64Array.from(arr, (v) => (v == null ? NaN : v));
  // Both schemas normalise to one shape: a polygon ring of per-vertex
  // columns. Schema 1 is the legacy 4-corner sidecar (c0..c3 in draw
  // order TL, TR, BR, BL — already a valid ring); schema 2 carries the
  // ring explicitly (ringSize = 4 * per_edge vertices, perimeter order).
  const cornerLat = raw.schema === 2
    ? raw.ringLat.map(toF64)
    : [toF64(raw.c0Lat), toF64(raw.c1Lat), toF64(raw.c2Lat), toF64(raw.c3Lat)];
  const cornerLon = raw.schema === 2
    ? raw.ringLon.map(toF64)
    : [toF64(raw.c0Lon), toF64(raw.c1Lon), toF64(raw.c2Lon), toF64(raw.c3Lon)];
  return {
    sampleCount: n,
    sampleRateHz: raw.sampleRateHz,
    tStart: raw.tStart,
    tStartMs: Date.parse(raw.tStart),
    t: toF64(raw.t),
    heliLat: toF64(raw.heliLat),
    heliLon: toF64(raw.heliLon),
    heliAltM: toF64(raw.heliAltM),
    ringSize: cornerLat.length,
    cornerLat,
    cornerLon,
    // Boresight ground hit per sample (baker ≥ 2026-09-07). Absent on
    // earlier sidecars — footprintCentre then derives it from the ring.
    centreLat: Array.isArray(raw.centreLat) ? toF64(raw.centreLat) : null,
    centreLon: Array.isArray(raw.centreLon) ? toF64(raw.centreLon) : null,
    flags: Uint8Array.from(raw.flags),
    maxGroundDistanceM: raw.horizonClip?.maxGroundDistanceM ?? 5000,
    fadeStartFraction: raw.horizonClip?.fadeStartFraction ?? 0.8,
  };
}

export function findFrameIndex(fp, tSec) {
  const t = fp.t;
  const n = t.length;
  if (n === 0) return { i0: 0, i1: 0, alpha: 0 };
  if (tSec <= t[0]) return { i0: 0, i1: 0, alpha: 0 };
  if (tSec >= t[n - 1]) return { i0: n - 1, i1: n - 1, alpha: 0 };
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (t[mid] <= tSec) lo = mid;
    else hi = mid;
  }
  const span = t[hi] - t[lo];
  const alpha = span > 0 ? (tSec - t[lo]) / span : 0;
  return { i0: lo, i1: hi, alpha };
}

export function interpolateCorners(fp, { i0, i1, alpha }) {
  const out = [];
  for (let k = 0; k < fp.cornerLat.length; k++) {
    const lat0 = fp.cornerLat[k][i0];
    const lon0 = fp.cornerLon[k][i0];
    const lat1 = fp.cornerLat[k][i1];
    const lon1 = fp.cornerLon[k][i1];
    out.push({
      lat: lat0 + (lat1 - lat0) * alpha,
      lon: lon0 + (lon1 - lon0) * alpha,
    });
  }
  return out;
}

export function interpolateHeli(fp, { i0, i1, alpha }) {
  return {
    lat: fp.heliLat[i0] + (fp.heliLat[i1] - fp.heliLat[i0]) * alpha,
    lon: fp.heliLon[i0] + (fp.heliLon[i1] - fp.heliLon[i0]) * alpha,
  };
}

const lerp = (a, b, alpha) => a + (b - a) * alpha;

// Where the segments a→c and b→d cross, in a local flat frame (lon scaled by
// cos(lat) so the two axes are metric-comparable). Null when parallel.
function intersectDiagonals(a, c, b, d) {
  const k = Math.cos(((a.lat + b.lat + c.lat + d.lat) / 4) * (Math.PI / 180));
  const ax = a.lon * k, ay = a.lat, bx = b.lon * k, by = b.lat;
  const rx = c.lon * k - ax, ry = c.lat - ay;   // a→c
  const sx = d.lon * k - bx, sy = d.lat - by;   // b→d
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < 1e-18) return null;
  const t = ((bx - ax) * sy - (by - ay) * sx) / denom;
  return { lat: ay + ry * t, lon: (ax + rx * t) / k };
}

/**
 * The ground point at the centre of the picture at telemetry time `tSec` —
 * the geotag every freeze frame carries.
 *
 * Prefers the baked boresight column (`centreLat`/`centreLon`), null where
 * the baker recorded the axis missing the ground. A sidecar from before that
 * column falls back to the crossing of the ring's diagonals: a homography
 * keeps lines straight and a rectangle's diagonals cross at its centre, so
 * on flat ground that IS the boresight hit. The fallback refuses frames
 * flagged horizon-clipped or DEM-missed, whose far vertices are the ray's
 * give-up points rather than ground.
 *
 * @returns {{lat:number, lon:number, source:"baked"|"derived"}|null}
 */
export function footprintCentre(fp, tSec) {
  if (!fp || fp.t.length === 0) return null;
  const { i0, i1, alpha } = findFrameIndex(fp, tSec);
  if (fp.centreLat !== null && fp.centreLon !== null) {
    const v = [fp.centreLat[i0], fp.centreLat[i1], fp.centreLon[i0], fp.centreLon[i1]];
    if (!v.every(Number.isFinite)) return null;
    return { lat: lerp(v[0], v[1], alpha), lon: lerp(v[2], v[3], alpha), source: "baked" };
  }
  if (fp.flags[i0] !== 0 || fp.flags[i1] !== 0) return null;
  // Corners sit at quarter points of the ring in both schemas: schema 1 is
  // the 4-corner ring itself; schema 2 walks TR → TL → BL → BR with
  // per_edge vertices between each pair.
  const q = fp.ringSize / 4;
  if (!Number.isInteger(q) || q < 1) return null;
  const corners = [0, q, 2 * q, 3 * q].map((k) => ({
    lat: lerp(fp.cornerLat[k][i0], fp.cornerLat[k][i1], alpha),
    lon: lerp(fp.cornerLon[k][i0], fp.cornerLon[k][i1], alpha),
  }));
  if (!corners.every((c) => Number.isFinite(c.lat) && Number.isFinite(c.lon))) return null;
  const x = intersectDiagonals(corners[0], corners[2], corners[1], corners[3]);
  return x === null ? null : { ...x, source: "derived" };
}

// ---------- Visibility toggle (map-corner control) ----------

/**
 * Mount the "Footprint" visibility toggle in the map's top-right corner.
 *
 * The design spec (§8.2) asks for a layer-control entry, default ON. This
 * viewer has no Leaflet layer control — map-level affordances are corner
 * buttons (see the basemap swap swatch in viewer.js) — so the toggle is one
 * of those, stacked directly under the swatch.
 *
 * State is deliberately not persisted: a reload always comes back at the
 * spec default (ON) rather than remembering the last toggle press. Same
 * non-persistence choice as the KMZ reference overlay's toggle (CLAUDE.md,
 * Plan 11) — that toggle's default is OFF, not ON; only the "don't persist"
 * decision is shared, not the default state.
 *
 * @param {HTMLElement | null | undefined} mapWrapEl `.vmc-map-wrapper` — the
 *   map element's parent, which also hosts the basemap swatch.
 * @param {{ controller?: { setVisible: (v: boolean) => void } | null }} [deps]
 *   `controller` is the value of `createFootprintController(...)`, or null when
 *   the flight has no footprint sidecar.
 * @returns {{ dispose: () => void }}
 */
export function mountFootprintToggle(mapWrapEl, deps = {}) {
  // No sidecar → no controller → no affordance. Guard first so viewer.js can
  // call this unconditionally: nothing below may touch the DOM in that case.
  const controller = deps?.controller;
  if (!controller || !mapWrapEl) return { dispose: () => {} };

  // Its own floating rail group, separated from the compass and the follow
  // pair (Hawk grouping: only the two follow styles are attached).
  const group = mapWrapEl.ownerDocument.createElement("div");
  group.className = "vmc-rail-group";
  const btn = mapWrapEl.ownerDocument.createElement("button");
  btn.type = "button";
  btn.className = "vmc-rail-btn vmc-footprint-toggle";
  btn.dataset.role = "footprint";
  btn.dataset.tour = "viewer.footprint";
  btn.setAttribute("aria-label", "Camera ground footprint");
  // Trapezoid: the frustum's ground shape, as a single rail glyph (the
  // rail's other buttons are one symbol each; text captions don't fit).
  btn.innerHTML = `<svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
    <polygon points="8,7 16,7 21,17 3,17" fill="none"
             stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
  </svg>`;
  let visible = true; // spec §8.2 — default ON, matching the controller's own
                      // initial state, so mounting drives nothing.

  function paint() {
    btn.setAttribute("aria-pressed", visible ? "true" : "false");
    btn.title = visible
      ? "Hide the camera ground footprint"
      : "Show the camera ground footprint";
  }
  paint();

  function onClick() {
    visible = !visible;
    controller.setVisible(visible);
    paint();
  }
  btn.addEventListener("click", onClick);
  group.appendChild(btn);
  mapWrapEl.appendChild(group);

  return {
    dispose: () => {
      btn.removeEventListener("click", onClick);
      group.remove();
    },
  };
}

// ---------- Leaflet controller ----------

export function createFootprintController({ L, map, video, fetchUrl, syncT }) {
  // The footprint and the heli marker are two views of the same telemetry, so
  // they must sample it at the same instant. `syncT` is viewer.js's video-time
  // -> telemetry-time map (it folds in the operator's GPS-sync nudge); without
  // it the two only agree at offset 0 and a nudge slides them apart.
  const telemetryTime = typeof syncT === "function"
    ? () => syncT(video.currentTime)
    : () => video.currentTime;
  let fp = null;
  let visible = true;
  let polygon = null;
  let raf = 0;
  let timeListener = null;
  let seekedListener = null;
  let playListener = null;
  let pauseListener = null;
  let gradient = null;      // <radialGradient> — the distance fade
  let gradientDefs = null;
  let gradientId = null;
  let lastHeli = null;      // last rendered heli, for zoom-driven refreshes
  let zoomListener = null;

  // Build a dedicated pane so the footprint sits above the path but below
  // markers. Under leaflet-rotate the pane must be parented into the
  // rotatePane: layer points are computed in the rotated frame, but the
  // plugin's rotate-aware createPane is commented out, so a container-less
  // custom pane lands under the unrotated mapPane and heading-up follow
  // renders the polygon skewed by the map bearing. Stock Leaflet has no
  // rotatePane; fall back to the default parent there.
  if (!map.getPane("vmcFootprintFill")) {
    const rotatePane = map.getPane("rotatePane");
    const p1 = rotatePane
      ? map.createPane("vmcFootprintFill", rotatePane)
      : map.createPane("vmcFootprintFill");
    p1.style.zIndex = "410";
  }

  // A layer on a custom pane gets a per-pane renderer at Leaflet's default
  // padding (0.1), ignoring the map's `renderer` option — so the padding has
  // to be restated here or the footprint clips ~10% into a drag.
  const renderer = L.svg({ pane: "vmcFootprintFill", padding: 0.5 });

  function makePrimitives() {
    polygon = L.polygon([[0, 0], [0, 0], [0, 0], [0, 0]], {
      pane: "vmcFootprintFill", renderer,
      color: STROKE_COLOR, weight: STROKE_WEIGHT, opacity: STROKE_OPACITY,
      fillColor: FILL_COLOR, fillOpacity: FILL_OPACITY,
      // Purely visual. The pane sits above the flight path and ghost
      // tracks, and an interactive path would swallow every click and
      // hover it covers — no click-to-scrub, no switching flights in a set
      // under the polygon. Non-interactive → pointer-events: none.
      interactive: false,
    });
  }

  function attach() {
    if (polygon === null) return;
    polygon.addTo(map);
  }

  function detach() {
    if (polygon !== null) map.removeLayer(polygon);
  }

  // --- Distance fade: a user-space radial gradient centred on the heli,
  // opaque out to FADE_START_FRACTION of the ray reach, transparent AT the
  // reach. Painted onto fill AND stroke, so a horizon-clipped polygon's far
  // edge dissolves instead of drawing a hard back line. Guards let the
  // node test-suite's SVG-less stubs run the same render path untouched.
  function ensureGradient() {
    if (gradient !== null) return gradient;
    const container = renderer?._container;
    if (!container || !container.ownerDocument) return null;
    const doc = container.ownerDocument;
    gradientId = `vmc-fp-fade-${Math.random().toString(36).slice(2)}`;
    gradientDefs = doc.createElementNS(SVG_NS, "defs");
    gradient = doc.createElementNS(SVG_NS, "radialGradient");
    gradient.setAttribute("id", gradientId);
    gradient.setAttribute("gradientUnits", "userSpaceOnUse");
    for (const [offset, opacity] of [[0, 1], [FADE_START_FRACTION, 1], [1, 0]]) {
      const stop = doc.createElementNS(SVG_NS, "stop");
      stop.setAttribute("offset", String(offset));
      stop.setAttribute("stop-color", FILL_COLOR);
      stop.setAttribute("stop-opacity", String(opacity));
      gradient.appendChild(stop);
    }
    gradientDefs.appendChild(gradient);
    container.appendChild(gradientDefs);
    return gradient;
  }

  function updateGradientGeometry(heli) {
    if (fp === null || heli == null || typeof map.latLngToLayerPoint !== "function") {
      return;
    }
    const g = ensureGradient();
    if (g === null) return;
    const centre = map.latLngToLayerPoint([heli.lat, heli.lon]);
    // Pixel radius of the reach: probe a point maxGroundDistanceM due east
    // and measure the projected gap — no zoom/CRS math to keep in sync.
    const dLonDeg = ((fp.maxGroundDistanceM
      / (R_EARTH_M * Math.cos((heli.lat * Math.PI) / 180))) * 180) / Math.PI;
    const east = map.latLngToLayerPoint([heli.lat, heli.lon + dLonDeg]);
    g.setAttribute("cx", String(centre.x));
    g.setAttribute("cy", String(centre.y));
    g.setAttribute("r", String(Math.hypot(east.x - centre.x, east.y - centre.y)));
  }

  function applyGradientPaint() {
    // Leaflet's _updateStyle rewrites the paint attributes on every
    // setStyle, so the gradient is re-asserted after each render.
    const path = polygon?._path;
    if (!path || gradientId === null) return;
    path.setAttribute("fill", `url(#${gradientId})`);
    path.setAttribute("stroke", `url(#${gradientId})`);
  }

  function render(tSec) {
    if (fp === null || polygon === null || !visible) return;
    const idx = findFrameIndex(fp, tSec);
    const corners = interpolateCorners(fp, idx);
    const heli = interpolateHeli(fp, idx);
    // DEM-miss / horizon-clip frames may have non-finite corners (see
    // baker._round_or_null). Hide the polygon for the frame
    // instead of stretching to (0,0). The flag bits are an additional
    // signal but the geometry check is the source of truth.
    const cornersFinite = corners.every(
      (c) => Number.isFinite(c.lat) && Number.isFinite(c.lon),
    );
    const heliFinite = Number.isFinite(heli.lat) && Number.isFinite(heli.lon);
    if (!cornersFinite || !heliFinite) {
      polygon.setStyle({ opacity: 0, fillOpacity: 0 });
      return;
    }
    polygon.setLatLngs(corners.map((c) => [c.lat, c.lon]));

    // Restore the base opacities (a hidden DEM-miss frame zeroed them);
    // the distance fade itself is the radial gradient, not a flat factor.
    polygon.setStyle({ fillOpacity: FILL_OPACITY, opacity: STROKE_OPACITY });
    lastHeli = heli;
    updateGradientGeometry(heli);
    applyGradientPaint();
  }

  function startRafLoop() {
    if (raf !== 0) return;
    const tick = () => {
      raf = 0;
      if (!video.paused && !video.ended) {
        render(telemetryTime());
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
  }
  function stopRafLoop() {
    if (raf !== 0) cancelAnimationFrame(raf);
    raf = 0;
  }

  async function load() {
    // Plain fetch, no `credentials` — `fetchUrl` is a cross-origin presigned
    // R2 URL and R2 never sends Access-Control-Allow-Credentials, so
    // `credentials: "include"` makes the browser reject the response and the
    // overlay dies silently. Matches every other viewer fetch of this URL
    // class (track.json, meta.json, the mp4 stream).
    const resp = await fetch(fetchUrl);
    if (!resp.ok) {
      console.warn("Footprint: fetch failed", resp.status, fetchUrl);
      return;
    }
    let raw;
    try { raw = await resp.json(); }
    catch (e) { console.warn("Footprint: invalid JSON", e); return; }
    try { fp = parseFootprintJSON(raw); }
    catch (e) { console.warn("Footprint:", e.message); return; }
    makePrimitives();
    attach();
    timeListener = () => render(telemetryTime());
    seekedListener = () => render(telemetryTime());
    playListener = () => startRafLoop();
    pauseListener = () => stopRafLoop();
    video.addEventListener("timeupdate", timeListener);
    video.addEventListener("seeked", seekedListener);
    video.addEventListener("play", playListener);
    video.addEventListener("pause", pauseListener);
    // Layer points rescale with the zoom, so the fade circle must follow
    // without waiting for the next video event.
    zoomListener = () => {
      if (lastHeli !== null) {
        updateGradientGeometry(lastHeli);
        applyGradientPaint();
      }
    };
    map.on?.("zoomend viewreset", zoomListener);
    render(telemetryTime());
    if (!video.paused) startRafLoop();
  }

  load().catch((e) => console.warn("Footprint: load error", e));

  return {
    setVisible(v) {
      visible = v;
      if (v) {
        attach();
        // The primitives hold whatever ring was current when they were
        // hidden; re-render so re-showing never flashes a stale polygon.
        if (fp !== null) render(telemetryTime());
      } else {
        detach();
      }
    },
    // Re-render at the current telemetry time without a video event. Called
    // when the GPS-sync offset changes: a nudge while paused fires no
    // `timeupdate`/`seeked`, so the polygon would otherwise stay behind
    // while the heli marker moves.
    refresh() {
      if (fp !== null) render(telemetryTime());
    },
    // The picture-centre ground point at a telemetry time (see
    // footprintCentre); null until the sidecar has loaded. Freeze frames
    // geotag with this rather than the aircraft position.
    centreAt(tSec) {
      return fp === null ? null : footprintCentre(fp, tSec);
    },
    destroy() {
      stopRafLoop();
      if (timeListener) video.removeEventListener("timeupdate", timeListener);
      if (seekedListener) video.removeEventListener("seeked", seekedListener);
      if (playListener) video.removeEventListener("play", playListener);
      if (pauseListener) video.removeEventListener("pause", pauseListener);
      if (zoomListener) map.off?.("zoomend viewreset", zoomListener);
      gradientDefs?.remove();
      gradient = null; gradientDefs = null; gradientId = null; lastHeli = null;
      detach();
      polygon = null; fp = null;
    },
  };
}
